import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Trash, Warning, UploadSimple, Copy, CaretDown, CaretUp } from '@phosphor-icons/react';
import { resourceIcon, RESOURCE_ICON_OPTIONS } from '@/lib/resourceIcons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = [
  { key: '0', label: 'Segunda-feira' },
  { key: '1', label: 'Terça-feira' },
  { key: '2', label: 'Quarta-feira' },
  { key: '3', label: 'Quinta-feira' },
  { key: '4', label: 'Sexta-feira' },
  { key: '5', label: 'Sábado' },
  { key: '6', label: 'Domingo' },
];

const CONFIG_TABS = [
  { value: 'clinica', label: 'Clínica' },
  { value: 'consultas', label: 'Consultas' },
  { value: 'planos', label: 'Planos e conteúdos' },
  { value: 'ementas_bebe', label: 'Ementas de bebé' },
  { value: 'documentos', label: 'Documentos' },
  { value: 'manutencao', label: 'Manutenção' },
];

function DailyScheduleSection() {
  const [schedule, setSchedule] = useState({});
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tRes] = await Promise.all([axios.get(`${API}/api/patient-tags`)]);
        setTags(tRes.data.tags || []);
        const stored = localStorage.getItem('daily_schedule_cache');
        if (stored) {
          setSchedule(JSON.parse(stored));
        } else {
          setSchedule({
            '0': { types: ['geral'], label: 'Acompanhamento geral' },
            '1': { types: ['gravida'], label: 'Acompanhamento de grávidas' },
            '2': { types: ['pos_parto'], label: 'Acompanhamento pós-parto' },
            '3': { types: ['introducao_alimentar'], label: 'Introdução alimentar' },
            '4': { types: ['geral'], label: 'Acompanhamento geral' },
            '5': { types: [], label: 'Dia livre' },
            '6': { types: [], label: 'Dia livre' },
          });
        }
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const toggleTag = (dayKey, tagValue) => {
    setSchedule(prev => {
      const day = prev[dayKey] || { types: [], label: '' };
      const types = day.types || [];
      const newTypes = types.includes(tagValue) ? types.filter(t => t !== tagValue) : [...types, tagValue];
      const newLabel = newTypes.length > 0
        ? newTypes.map(t => tags.find(tg => tg.value === t)?.label || t).join(' + ')
        : 'Dia livre';
      return { ...prev, [dayKey]: { ...day, types: newTypes, label: newLabel } };
    });
  };

  const updateLabel = (dayKey, label) => {
    setSchedule(prev => ({ ...prev, [dayKey]: { ...prev[dayKey], label } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/daily-schedule`, { schedule });
      localStorage.setItem('daily_schedule_cache', JSON.stringify(schedule));
      toast.success('Configuração guardada');
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-brand-muted">A carregar...</div>;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="daily-schedule-section">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Grupos de foco diários</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Define quais os tipos de pacientes que aparecem no dashboard de cada dia da semana</p>

      <div className="space-y-3 mb-4">
        {DAYS.map(day => {
          const cfg = schedule[day.key] || { types: [], label: '' };
          const selectedTypes = cfg.types || [];
          return (
            <div key={day.key} className="border border-brand-border rounded-xl p-3" data-testid={`day-config-${day.key}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-heading font-medium text-sm text-brand-text">{day.label}</h4>
                {selectedTypes.length === 0 && <span className="text-[10px] text-brand-placeholder">Dia livre</span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map(tag => (
                  <button key={tag.value} onClick={() => toggleTag(day.key, tag.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedTypes.includes(tag.value) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
                    data-testid={`day-${day.key}-tag-${tag.value}`}>
                    {tag.label}
                  </button>
                ))}
              </div>
              <Input value={cfg.label} onChange={e => updateLabel(day.key, e.target.value)} placeholder="Descrição do dia (ex: Acompanhamento de grávidas)" className="rounded-lg border-brand-border text-xs h-8" />
            </div>
          );
        })}
      </div>

      <button onClick={save} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="save-schedule-btn">
        {saving ? 'A guardar...' : 'Guardar configuração'}
      </button>
    </div>
  );
}

export default function AdminSchedulePage() {
  useRequireAdmin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = CONFIG_TABS.some(t => t.value === searchParams.get('tab')) ? searchParams.get('tab') : 'clinica';
  const setActiveTab = (tab) => navigate(`/admin/configuracoes?tab=${tab}`, { replace: true });

  return (
    <div className="fade-in" data-testid="admin-schedule-page">
      <div className="mb-6">
        <h2 className="font-heading font-medium text-2xl text-brand-text">Configurações</h2>
        <p className="text-sm text-brand-muted mt-1">Informação legal, consultas, conteúdos, ementas de bebé, documentos e manutenção</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex-wrap gap-0 bg-white border border-brand-border rounded-2xl p-1 mb-5 h-auto" data-testid="config-tabs">
          {CONFIG_TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid={`config-tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="clinica" className="space-y-6">
          <LegalInfoConfigSection />
        </TabsContent>

        <TabsContent value="consultas" className="space-y-6">
          <DailyScheduleSection />
          <ConsultationOriginsConfigSection />
        </TabsContent>

        <TabsContent value="planos" className="space-y-6">
          <MealSlotsConfigSection />
          <ResourceTypeCategoriesConfigSection />
        </TabsContent>

        <TabsContent value="ementas_bebe" className="space-y-6">
          <BabyMenuAiRulesConfigSection />
          <BabyAllergenToleranceSection />
          <BabyMenuPhaseRulesConfigSection />
        </TabsContent>

        <TabsContent value="documentos" className="space-y-6">
          <DeclarationTemplatesConfigSection />
        </TabsContent>

        <TabsContent value="manutencao" className="space-y-6">
          <MaintenanceSection />
          <PaymentDuplicatesSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const DECLARATION_INSERT_TAGS = ['nome_paciente', 'data_nascimento_paciente', 'nome_encarregado', 'data_consulta', 'hora_inicio', 'hora_fim', 'nome_nutricionista', 'cedula', 'data_atual', 'local', 'conteudo'];

function DeclarationTemplatesConfigSection() {
  const confirmDelete = useConfirmDelete();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [previewing, setPreviewing] = useState('');
  const [previews, setPreviews] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const textareaRefs = useRef({});

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/declaration-templates`); setTemplates(data || []); }
    catch { toast.error('Erro ao carregar modelos'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (id, field, value) => setTemplates(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));

  const insertVar = (id, field, tag) => {
    const key = `${id}_${field}`;
    const el = textareaRefs.current[key];
    const current = templates.find(t => t.id === id)?.[field] || '';
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}{${tag}}${current.slice(end)}`;
    update(id, field, next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + tag.length + 2;
      el.focus(); el.setSelectionRange(pos, pos);
    });
  };

  const save = async (t) => {
    setSaving(t.id);
    try { await axios.put(`${API}/api/admin/declaration-templates/${t.id}`, { name: t.name, text_pt: t.text_pt, text_en: t.text_en, active: t.active }); toast.success('Modelo guardado'); }
    catch { toast.error('Erro ao guardar'); }
    finally { setSaving(''); }
  };
  const addTemplate = async () => {
    try { const { data } = await axios.post(`${API}/api/admin/declaration-templates`, { name: 'Novo modelo', text_pt: '', text_en: '' }); setTemplates(prev => [...prev, data]); }
    catch { toast.error('Erro ao criar modelo'); }
  };
  const duplicate = async (id) => {
    try { const { data } = await axios.post(`${API}/api/admin/declaration-templates/${id}/duplicate`); setTemplates(prev => [...prev, data]); toast.success('Modelo duplicado'); }
    catch { toast.error('Erro ao duplicar'); }
  };
  const remove = async (id) => {
    const t = templates.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o modelo de declaração "${t?.name || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/admin/declaration-templates/${id}`); setTemplates(prev => prev.filter(t => t.id !== id)); toast.success('Modelo eliminado'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const previewTest = async (t) => {
    setPreviewing(t.id);
    try {
      const { data } = await axios.post(`${API}/api/admin/declaration-templates/preview-test`, { text_pt: t.text_pt, text_en: t.text_en });
      setPreviews(prev => ({ ...prev, [t.id]: data }));
    } catch { toast.error('Erro ao pré-visualizar'); }
    finally { setPreviewing(''); }
  };

  if (loading) return null;

  const activeTemplates = templates.filter(t => t.active !== false);
  const archivedTemplates = templates.filter(t => t.active === false);

  const renderCard = (t) => (
    <div key={t.id} className="border border-brand-border rounded-xl p-3 space-y-2" data-testid={`declaration-template-${t.id}`}>
      <div className="flex items-center gap-2">
        <Input value={t.name} onChange={e => update(t.id, 'name', e.target.value)} className="rounded-lg border-brand-border text-sm flex-1" data-testid={`declaration-template-name-${t.id}`} />
        <button onClick={() => update(t.id, 'active', !t.active)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${t.active !== false ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`declaration-template-toggle-${t.id}`}>{t.active !== false ? 'Ativo' : 'Arquivado'}</button>
        <button onClick={() => duplicate(t.id)} className="text-brand-muted hover:text-brand-green flex-shrink-0" title="Duplicar" data-testid={`declaration-template-duplicate-${t.id}`}><Copy size={14} /></button>
        <button onClick={() => remove(t.id)} disabled={t.in_use} title={t.in_use ? 'Já usado em declarações geradas, só pode ser arquivado' : 'Eliminar'} className="text-brand-muted hover:text-red-500 flex-shrink-0 disabled:opacity-30 disabled:hover:text-brand-muted" data-testid={`declaration-template-delete-${t.id}`}><Trash size={14} /></button>
      </div>
      {t.in_use && <p className="text-[10px] text-brand-muted">Já foi usado em declarações geradas, só pode ser arquivado, não eliminado.</p>}
      <div>
        <Label className="text-[10px] text-brand-muted mb-1 block">Texto PT</Label>
        <Textarea ref={el => { textareaRefs.current[`${t.id}_text_pt`] = el; }} value={t.text_pt || ''} onChange={e => update(t.id, 'text_pt', e.target.value)} className="rounded-lg border-brand-border text-xs" rows={3} data-testid={`declaration-template-pt-${t.id}`} />
        <div className="flex flex-wrap gap-1 mt-1">
          {DECLARATION_INSERT_TAGS.map(tag => <button key={tag} type="button" onClick={() => insertVar(t.id, 'text_pt', tag)} className="text-[10px] px-1.5 py-0.5 rounded bg-brand-bg border border-brand-border text-brand-muted hover:bg-brand-green/10 hover:text-brand-green" data-testid={`declaration-insert-pt-${t.id}-${tag}`}>{`{${tag}}`}</button>)}
        </div>
      </div>
      <div>
        <Label className="text-[10px] text-brand-muted mb-1 block">Texto EN</Label>
        <Textarea ref={el => { textareaRefs.current[`${t.id}_text_en`] = el; }} value={t.text_en || ''} onChange={e => update(t.id, 'text_en', e.target.value)} className="rounded-lg border-brand-border text-xs" rows={3} data-testid={`declaration-template-en-${t.id}`} />
        <div className="flex flex-wrap gap-1 mt-1">
          {DECLARATION_INSERT_TAGS.map(tag => <button key={tag} type="button" onClick={() => insertVar(t.id, 'text_en', tag)} className="text-[10px] px-1.5 py-0.5 rounded bg-brand-bg border border-brand-border text-brand-muted hover:bg-brand-green/10 hover:text-brand-green" data-testid={`declaration-insert-en-${t.id}-${tag}`}>{`{${tag}}`}</button>)}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => save(t)} disabled={saving === t.id} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50" data-testid={`declaration-template-save-${t.id}`}>{saving === t.id ? 'A guardar...' : 'Guardar'}</button>
        <button onClick={() => previewTest(t)} disabled={previewing === t.id} className="text-xs text-brand-muted font-medium disabled:opacity-50" data-testid={`declaration-template-preview-${t.id}`}>{previewing === t.id ? 'A pré-visualizar...' : 'Pré-visualizar (dados de teste)'}</button>
      </div>
      {previews[t.id] && (
        <div className="bg-brand-bg rounded-lg p-2 space-y-1" data-testid={`declaration-template-preview-result-${t.id}`}>
          <p className="text-[10px] text-brand-muted"><span className="font-medium">PT:</span> {previews[t.id].final_text_pt}</p>
          <p className="text-[10px] text-brand-muted"><span className="font-medium">EN:</span> {previews[t.id].final_text_en}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="declaration-templates-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Modelos de Declarações</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Usa os botões de variável abaixo de cada campo para inserir dados automáticos no texto.</p>
      <div className="space-y-4">
        {activeTemplates.map(renderCard)}
      </div>
      <button onClick={addTemplate} className="mt-3 text-xs text-brand-green font-medium" data-testid="declaration-template-add-btn">+ Novo modelo</button>

      {archivedTemplates.length > 0 && (
        <div className="mt-5 pt-4 border-t border-brand-border/50">
          <button onClick={() => setShowArchived(v => !v)} className="flex items-center gap-1.5 text-xs font-medium text-brand-muted" data-testid="declaration-templates-archived-toggle">
            <CaretDown size={13} className={`transition-transform ${showArchived ? 'rotate-180' : ''}`} /> Arquivados ({archivedTemplates.length})
          </button>
          {showArchived && <div className="space-y-4 mt-3">{archivedTemplates.map(renderCard)}</div>}
        </div>
      )}
    </div>
  );
}

function MaintenanceSection() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const openModal = async () => {
    setLoading(true); setOpen(true); setCount(null);
    try {
      const { data } = await axios.get(`${API}/api/admin/maintenance/test-users-count`);
      setCount(data.count);
    } catch (e) { toast.error('Erro ao contar utilizadores de teste'); setOpen(false); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/maintenance/cleanup-test-users`);
      toast.success(`Removidos ${data.users_removed} utilizadores de teste e ${data.records_removed} registos associados.`);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao limpar'); }
    finally { setRunning(false); }
  };

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="maintenance-section">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Manutenção</h3>
      <p className="text-xs text-brand-muted mb-3">Remove utilizadores de teste (email ou nome a começar por <code>TEST_</code>) e todos os dados associados.</p>
      <button onClick={openModal} className="bg-red-50 text-red-600 border border-red-200 rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-red-100 transition-colors" data-testid="cleanup-test-users-btn">
        <Trash size={15} /> Limpar utilizadores de teste
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="cleanup-confirm-modal">
          <DialogHeader><DialogTitle className="font-heading text-brand-text flex items-center gap-2"><Warning size={18} className="text-amber-500" weight="duotone" /> Limpar utilizadores de teste</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {loading ? (
              <p className="text-sm text-brand-muted">A analisar…</p>
            ) : count === 0 ? (
              <p className="text-sm text-brand-muted" data-testid="cleanup-none">Não existem utilizadores de teste para remover.</p>
            ) : (
              <p className="text-sm text-brand-text" data-testid="cleanup-count">
                Vão ser removidos <strong>{count}</strong> utilizador{count === 1 ? '' : 'es'} de teste (<code>TEST_</code>) e <strong>todos os dados associados</strong>. Esta ação é irreversível.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setOpen(false)} className="rounded-full px-4 py-2 text-sm text-brand-muted border border-brand-border" data-testid="cleanup-cancel-btn">Cancelar</button>
              {count > 0 && (
                <button onClick={confirm} disabled={running} className="bg-red-600 text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="cleanup-confirm-btn">
                  {running ? 'A remover…' : `Remover ${count}`}
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentDuplicatesSection() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/admin/payments/duplicates`); setGroups(data.groups || []); setTotal(data.total || 0); }
    catch { toast.error('Erro ao carregar duplicados'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const keep = async (group, keepId) => {
    setResolving(group.group_key);
    try {
      await axios.post(`${API}/api/admin/payments/duplicates/resolve`, { payment_ids: group.payments.map(p => p.id), keep_payment_id: keepId });
      toast.success('Resolvido, o outro pagamento foi arquivado (não apagado)');
      setGroups(prev => prev.filter(g => g.group_key !== group.group_key));
      setTotal(t => t - 1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setResolving(''); }
  };

  if (loading) return <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6 text-xs text-brand-muted" data-testid="payment-duplicates-loading">A analisar pagamentos…</div>;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="payment-duplicates-section">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Pagamentos possivelmente duplicados</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Mesma paciente, mesmo valor, datas próximas, um Stripe e outro não, provável registo em duplicado. Nada é apagado: escolhe qual manter, o outro fica arquivado no histórico.</p>
      {total === 0 ? (
        <p className="text-xs text-brand-muted" data-testid="payment-duplicates-empty">Sem duplicados detetados.</p>
      ) : (
        <div className="space-y-3">
          {groups.map(g => (
            <div key={g.group_key} className="border border-amber-200 bg-amber-50/40 rounded-xl p-3" data-testid={`duplicate-group-${g.group_key}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-brand-text">{g.patient_name} · {g.amount}€</p>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${g.confidence === 'alta' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`duplicate-confidence-${g.group_key}`}>Confiança {g.confidence}</span>
              </div>
              <div className="space-y-1.5">
                {g.payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5" data-testid={`duplicate-payment-${p.id}`}>
                    <span className="text-[11px] text-brand-text">{fmtDatePt(p.date)} · {p.method}{p.notes ? ` · ${p.notes}` : ''}</span>
                    <button onClick={() => keep(g, p.id)} disabled={resolving === g.group_key} className="text-[10px] bg-brand-green text-white rounded-full px-2.5 py-1 disabled:opacity-50 flex-shrink-0 ml-2" data-testid={`keep-payment-${p.id}`}>Manter este</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function MealSlotsConfigSection() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/settings/meal-slots`);
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
      } catch {
        // silent
      } finally { setLoading(false); }
    })();
  }, []);

  const updateSlot = (idx, field, value) => {
    setSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const removeSlot = (idx) => {
    setSlots(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
  };

  const addSlot = () => {
    const order = slots.length;
    setSlots(prev => [...prev, { key: `refeicao_${Date.now()}`, label: 'Nova refeição', order }]);
  };

  const moveSlot = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= slots.length) return;
    setSlots(prev => {
      const out = [...prev];
      [out[idx], out[ni]] = [out[ni], out[idx]];
      return out.map((s, i) => ({ ...s, order: i }));
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = slots
        .map((s, i) => ({ key: (s.key || '').trim(), label: (s.label || '').trim(), order: i }))
        .filter(s => s.key && s.label);
      if (!cleaned.length) { toast.error('Adiciona pelo menos uma refeição'); return; }
      const { data } = await axios.put(`${API}/api/settings/meal-slots`, { slots: cleaned });
      setSlots(Array.isArray(data?.slots) ? data.slots : cleaned);
      toast.success('Refeições guardadas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar');
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="meal-slots-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Refeições do plano</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Define os momentos de refeição usados em planos alimentares e na alocação de receitas. A chave (interna) deve ser única, sem espaços.</p>
      <div className="space-y-2">
        {slots.map((s, idx) => (
          <div key={idx} className="flex items-center gap-2" data-testid={`meal-slot-row-${idx}`}>
            <div className="flex flex-col">
              <button onClick={() => moveSlot(idx, -1)} disabled={idx === 0} className="text-brand-muted hover:text-brand-green disabled:opacity-30 text-xs leading-none" title="Subir">▲</button>
              <button onClick={() => moveSlot(idx, 1)} disabled={idx === slots.length - 1} className="text-brand-muted hover:text-brand-green disabled:opacity-30 text-xs leading-none" title="Descer">▼</button>
            </div>
            <Input
              value={s.label}
              onChange={e => updateSlot(idx, 'label', e.target.value)}
              placeholder="Etiqueta visível (ex: Almoço)"
              className="rounded-lg border-brand-border text-xs h-8 flex-1"
              data-testid={`meal-slot-label-${idx}`}
            />
            <Input
              value={s.key}
              onChange={e => updateSlot(idx, 'key', e.target.value.replace(/\s+/g, '_').toLowerCase())}
              placeholder="chave_interna"
              className="rounded-lg border-brand-border text-xs h-8 w-40 font-mono"
              data-testid={`meal-slot-key-${idx}`}
            />
            <button onClick={() => removeSlot(idx)} className="text-brand-muted hover:text-red-500 text-xs px-2" title="Remover" data-testid={`meal-slot-remove-${idx}`}>×</button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={addSlot} className="text-xs text-brand-green hover:underline" data-testid="meal-slot-add">+ Adicionar refeição</button>
        <button onClick={save} disabled={saving} className="ml-auto bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium disabled:opacity-50" data-testid="meal-slot-save">
          {saving ? 'A guardar…' : 'Guardar refeições'}
        </button>
      </div>
    </div>
  );
}

function ConsultationOriginsConfigSection() {
  const confirmDelete = useConfirmDelete();
  const [origins, setOrigins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/consultation-origins?all=true`);
      setOrigins(Array.isArray(data) ? data : []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addOrigin = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Indica o nome da origem'); return; }
    try { await axios.post(`${API}/api/consultation-origins`, { name }); setNewName(''); toast.success('Origem adicionada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const renameOrigin = async (o, name) => {
    setOrigins(prev => prev.map(x => x.id === o.id ? { ...x, name } : x));
  };
  const commitRename = async (o) => {
    const name = (o.name || '').trim();
    if (!name) { toast.error('Nome obrigatório'); load(); return; }
    try { await axios.put(`${API}/api/consultation-origins/${o.id}`, { name }); }
    catch { toast.error('Erro ao guardar nome'); load(); }
  };
  const toggleActive = async (o) => {
    try { await axios.put(`${API}/api/consultation-origins/${o.id}`, { active: !o.active }); load(); }
    catch { toast.error('Erro'); }
  };
  const removeOrigin = async (o) => {
    const ok = await confirmDelete({ description: `Vai apagar a origem "${o.name}".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/consultation-origins/${o.id}`); toast.success('Origem eliminada'); load(); }
    catch { toast.error('Erro'); }
  };
  const move = async (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= origins.length) return;
    const out = [...origins];
    [out[idx], out[ni]] = [out[ni], out[idx]];
    setOrigins(out);
    try { await axios.put(`${API}/api/consultation-origins/reorder/all`, { ids: out.map(o => o.id) }); }
    catch { toast.error('Erro ao reordenar'); load(); }
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="consultation-origins-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Origens de 1.ª Consulta</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Define como os pacientes conheceram o serviço. Apenas as origens ativas aparecem no campo obrigatório da 1.ª consulta.</p>
      <div className="space-y-2">
        {origins.map((o, idx) => (
          <div key={o.id} className="flex items-center gap-2" data-testid={`origin-row-${idx}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-brand-muted hover:text-brand-green disabled:opacity-30 text-xs leading-none" title="Subir">▲</button>
              <button onClick={() => move(idx, 1)} disabled={idx === origins.length - 1} className="text-brand-muted hover:text-brand-green disabled:opacity-30 text-xs leading-none" title="Descer">▼</button>
            </div>
            <Input value={o.name} onChange={e => renameOrigin(o, e.target.value)} onBlur={() => commitRename(o)} className="rounded-lg border-brand-border text-xs h-8 flex-1" data-testid={`origin-name-${idx}`} />
            <button onClick={() => toggleActive(o)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${o.active ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`origin-toggle-${idx}`}>{o.active ? 'Ativa' : 'Inativa'}</button>
            <button onClick={() => removeOrigin(o)} className="text-brand-muted hover:text-red-500 text-xs px-2" title="Remover" data-testid={`origin-remove-${idx}`}>×</button>
          </div>
        ))}
        {origins.length === 0 && <p className="text-xs text-brand-placeholder">Sem origens configuradas.</p>}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addOrigin(); }} placeholder="Nova origem (ex: Facebook)" className="rounded-lg border-brand-border text-xs h-8 flex-1" data-testid="origin-new-input" />
        <button onClick={addOrigin} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid="origin-add-btn">+ Adicionar</button>
      </div>
    </div>
  );
}

function BabyMenuAiRulesConfigSection() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/baby-menu-ai-rules`); setRules(data); }
    catch { toast.error('Erro ao carregar regras'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (field, value) => setRules(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/baby-menu-ai-rules`, rules);
      setRules(data);
      toast.success('Regras guardadas');
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  if (!rules) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="baby-menu-ai-rules-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Regras Clínicas: Ementas de Bebé</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Regras que qualquer geração automática de ementas de introdução alimentar deve seguir. Ficam vazias até as preencheres.</p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Idade de início por defeito (meses)</Label>
          <Input type="number" value={rules.default_start_age_months ?? ''} onChange={e => set('default_start_age_months', e.target.value === '' ? null : Number(e.target.value))} className="rounded-lg border-brand-border text-sm w-32" data-testid="ai-rules-start-age" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Abordagem por defeito</Label>
          <select value={rules.default_approach || ''} onChange={e => set('default_approach', e.target.value)} className="border border-brand-border rounded-lg text-sm h-9 px-2 bg-white" data-testid="ai-rules-approach">
            <option value="">Não definido</option>
            <option value="tradicional">Tradicional</option>
            <option value="blw">BLW</option>
            <option value="mista">Mista</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Ordem de introdução de alergénios</Label>
          <AllergenOrderListEditor order={rules.allergen_intro_order_list || []} onChange={(v) => set('allergen_intro_order_list', v)} />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Notas sobre a ordem de introdução</Label>
          <Textarea value={rules.allergen_intro_order || ''} onChange={e => set('allergen_intro_order', e.target.value)} className="rounded-lg border-brand-border text-sm" rows={2} data-testid="ai-rules-allergen-order" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Alimentos a evitar sempre</Label>
          <Textarea value={rules.foods_to_avoid || ''} onChange={e => set('foods_to_avoid', e.target.value)} className="rounded-lg border-brand-border text-sm" rows={2} data-testid="ai-rules-foods-avoid" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Limite semanal de peixe/marisco (inclui peixe na escola)</Label>
          <Input type="number" min={1} value={rules.fish_weekly_limit ?? 4} onChange={e => set('fish_weekly_limit', e.target.value === '' ? null : Number(e.target.value))} className="rounded-lg border-brand-border text-sm w-32" data-testid="ai-rules-fish-limit" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Mínimo semanal de peixe/marisco (inclui peixe na escola)</Label>
          <Input type="number" min={0} value={rules.fish_weekly_min_limit ?? 2} onChange={e => set('fish_weekly_min_limit', e.target.value === '' ? null : Number(e.target.value))} className="rounded-lg border-brand-border text-sm w-32" data-testid="ai-rules-fish-min-limit" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Limite semanal de repetição do mesmo alimento (exceto "Uso diário")</Label>
          <Input type="number" min={1} value={rules.food_weekly_repeat_limit ?? 3} onChange={e => set('food_weekly_repeat_limit', e.target.value === '' ? null : Number(e.target.value))} className="rounded-lg border-brand-border text-sm w-32" data-testid="ai-rules-food-repeat-limit" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Regras em texto livre</Label>
          <Textarea value={rules.free_text_rules || ''} onChange={e => set('free_text_rules', e.target.value)} className="rounded-lg border-brand-border text-sm" rows={4} data-testid="ai-rules-free-text" />
        </div>
        <button onClick={save} disabled={saving} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" data-testid="ai-rules-save-btn">{saving ? 'A guardar...' : 'Guardar regras'}</button>
      </div>
    </div>
  );
}

function AllergenOrderListEditor({ order, onChange }) {
  const [categories, setCategories] = useState([]);
  useEffect(() => { axios.get(`${API}/api/baby-allergen-categories`).then(({ data }) => setCategories(data)).catch(() => {}); }, []);
  const remaining = categories.filter(c => !order.includes(c));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const u = [...order];
    [u[i], u[j]] = [u[j], u[i]];
    onChange(u);
  };
  const remove = (i) => onChange(order.filter((_, idx) => idx !== i));
  const add = (c) => onChange([...order, c]);
  return (
    <div>
      <div className="space-y-1 mb-1.5">
        {order.length === 0 && <span className="text-[10px] text-brand-placeholder">Nenhum tipo na ordem</span>}
        {order.map((c, i) => (
          <div key={c} className="flex items-center gap-1.5 rounded-lg px-2 py-1 bg-brand-bg" data-testid={`allergen-order-item-${i}`}>
            <span className="text-[10px] font-semibold text-brand-green w-4">{i + 1}.</span>
            <span className="text-[10px] flex-1 text-brand-text">{c}</span>
            <div className="flex items-center gap-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-brand-muted hover:text-brand-green disabled:opacity-30 p-0.5" data-testid={`allergen-order-up-${i}`}><CaretUp size={11} /></button>
              <button onClick={() => move(i, 1)} disabled={i === order.length - 1} className="text-brand-muted hover:text-brand-green disabled:opacity-30 p-0.5" data-testid={`allergen-order-down-${i}`}><CaretDown size={11} /></button>
              <button onClick={() => remove(i)} className="text-brand-muted hover:text-red-500 p-0.5" data-testid={`allergen-order-remove-${i}`}><Trash size={11} /></button>
            </div>
          </div>
        ))}
      </div>
      {remaining.length > 0 && (
        <select value="" onChange={e => { if (e.target.value) add(e.target.value); }} className="border border-brand-border rounded-lg text-xs h-8 px-2 bg-white" data-testid="allergen-order-add-select">
          <option value="">+ Adicionar tipo à ordem...</option>
          {remaining.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
    </div>
  );
}

function BabyAllergenToleranceSection() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/baby-allergen-category-tolerance`); setRows(data); }
    catch { toast.error('Erro ao carregar tolerância de alergénios'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (category, value) => {
    setRows(prev => prev.map(r => r.category === category ? { ...r, tolerance_full_type: value } : r));
    try { await axios.put(`${API}/api/admin/baby-allergen-category-tolerance/${encodeURIComponent(category)}`, { tolerance_full_type: value }); }
    catch { toast.error('Erro ao guardar'); load(); }
  };

  if (!rows.length) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="baby-allergen-tolerance-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Tolerância por tipo de alergénio</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Quando "vale para todo o tipo" está ativo, um alimento tolerado liberta todos os alimentos desse tipo sem novo intervalo de introdução. Quando desativado, cada alimento do tipo precisa da sua própria introdução (ex.: Marisco, Frutos de casca rija).</p>
      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r.category} className="flex items-center justify-between py-1" data-testid={`allergen-tolerance-row-${r.category}`}>
            <span className="text-xs text-brand-text">{r.category}</span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-brand-muted">{r.tolerance_full_type ? 'Vale para todo o tipo' : 'Alimento a alimento'}</span>
              <Switch checked={r.tolerance_full_type} onCheckedChange={(v) => toggle(r.category, v)} data-testid={`allergen-tolerance-switch-${r.category}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BabyMenuPhaseRulesConfigSection() {
  const [phases, setPhases] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/baby-menu-phase-rules`); setPhases(data); }
    catch { toast.error('Erro ao carregar fases'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (key, field, value) => setPhases(prev => prev.map(p => p.key === key ? { ...p, [field]: value } : p));

  const save = async (key) => {
    const phase = phases.find(p => p.key === key);
    setSavingKey(key);
    try {
      await axios.put(`${API}/api/admin/baby-menu-phase-rules/${key}`, { texturas: phase.texturas, cortes_formatos: phase.cortes_formatos, refeicoes: phase.refeicoes, notas: phase.notas });
      toast.success(`${phase.label} guardada`);
    } catch { toast.error('Erro ao guardar'); }
    finally { setSavingKey(null); }
  };

  if (!phases) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="baby-menu-phase-rules-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Modelos por Fase: Ementas de Bebé</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">3 fases fixas. Ficam vazias até as preencheres.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {phases.map(p => (
          <div key={p.key} className="border border-brand-border rounded-xl p-3" data-testid={`phase-rule-card-${p.key}`}>
            <p className="text-xs font-medium text-brand-text mb-2">{p.label}</p>
            <div className="space-y-2">
              <div>
                <Label className="text-[10px] text-brand-muted mb-0.5 block">Texturas</Label>
                <Textarea value={p.texturas} onChange={e => setField(p.key, 'texturas', e.target.value)} className="rounded-lg border-brand-border text-xs" rows={2} data-testid={`phase-rule-texturas-${p.key}`} />
              </div>
              <div>
                <Label className="text-[10px] text-brand-muted mb-0.5 block">Cortes / Formatos</Label>
                <Textarea value={p.cortes_formatos} onChange={e => setField(p.key, 'cortes_formatos', e.target.value)} className="rounded-lg border-brand-border text-xs" rows={2} data-testid={`phase-rule-cortes-${p.key}`} />
              </div>
              <div>
                <Label className="text-[10px] text-brand-muted mb-0.5 block">Número e tipo de refeições</Label>
                <Textarea value={p.refeicoes} onChange={e => setField(p.key, 'refeicoes', e.target.value)} className="rounded-lg border-brand-border text-xs" rows={2} data-testid={`phase-rule-refeicoes-${p.key}`} />
              </div>
              <div>
                <Label className="text-[10px] text-brand-muted mb-0.5 block">Notas</Label>
                <Textarea value={p.notas} onChange={e => setField(p.key, 'notas', e.target.value)} className="rounded-lg border-brand-border text-xs" rows={2} data-testid={`phase-rule-notas-${p.key}`} />
              </div>
              <button onClick={() => save(p.key)} disabled={savingKey === p.key} className="w-full bg-brand-green text-white rounded-full py-1.5 text-[11px] font-medium disabled:opacity-50" data-testid={`phase-rule-save-${p.key}`}>{savingKey === p.key ? 'A guardar...' : 'Guardar'}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LegalInfoConfigSection() {
  const [legal, setLegal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/payment-settings`); setLegal(data.legal_info); }
    catch { toast.error('Erro ao carregar informação legal'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (field, value) => setLegal(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/payment-settings`, { legal_info: legal });
      setLegal(data.legal_info);
      toast.success('Informação legal guardada');
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/api/upload`, fd);
      set('livro_reclamacoes_logo_path', data.path);
    } catch { toast.error('Erro ao carregar imagem'); }
    finally { setUploading(false); }
  };

  if (!legal) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="legal-info-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Informação legal</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Mostrada no rodapé das páginas públicas, na área da paciente e na página "Informação legal".</p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Nome profissional</Label>
          <Input value={legal.professional_name || ''} onChange={e => set('professional_name', e.target.value)} className="rounded-lg border-brand-border text-sm" data-testid="legal-professional-name-input" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Cédula profissional</Label>
          <Input value={legal.cedula_profissional || ''} disabled className="rounded-lg border-brand-border text-sm w-40 bg-brand-bg/50 text-brand-muted cursor-not-allowed" data-testid="legal-cedula-input" />
          <p className="text-[10px] text-brand-placeholder mt-1">Vem de Definições (cédula do admin), para editar, vai a "Definições" no fundo do menu.</p>
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Nº registo ERS consultório</Label>
          <Input value={legal.ers_clinic || ''} onChange={e => set('ers_clinic', e.target.value)} className="rounded-lg border-brand-border text-sm w-40" data-testid="legal-ers-clinic-input" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Nº registo ERS telemedicina (opcional)</Label>
          <Input value={legal.ers_telemedicine || ''} onChange={e => set('ers_telemedicine', e.target.value)} placeholder="Deixa vazio se não aplicável" className="rounded-lg border-brand-border text-sm w-40" data-testid="legal-ers-telemedicine-input" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Link do Livro de Reclamações</Label>
          <Input value={legal.livro_reclamacoes_url || ''} onChange={e => set('livro_reclamacoes_url', e.target.value)} className="rounded-lg border-brand-border text-sm" data-testid="legal-livro-url-input" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">NIF</Label>
          <Input value={legal.nif || ''} onChange={e => set('nif', e.target.value)} className="rounded-lg border-brand-border text-sm w-40" data-testid="legal-nif-input" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Morada do estabelecimento</Label>
          <Input value={legal.morada || ''} onChange={e => set('morada', e.target.value)} className="rounded-lg border-brand-border text-sm" data-testid="legal-morada-input" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Telefone</Label>
          <Input value={legal.telefone || ''} onChange={e => set('telefone', e.target.value)} className="rounded-lg border-brand-border text-sm w-40" data-testid="legal-telefone-input" />
        </div>
        <div>
          <Label className="text-xs text-brand-muted mb-1 block">Logótipo oficial do Livro de Reclamações (opcional)</Label>
          <p className="text-[10px] text-brand-placeholder mb-1.5">Se não carregares imagem, mostra-se um link de texto "Livro de Reclamações Eletrónico".</p>
          <div className="flex items-center gap-3">
            {legal.livro_reclamacoes_logo_path && (
              <img src={`${API}/api/files/${legal.livro_reclamacoes_logo_path}`} alt="Logótipo" className="h-10 border border-brand-border rounded-lg p-1" data-testid="legal-logo-preview" />
            )}
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-brand-border cursor-pointer hover:border-brand-green text-xs text-brand-muted" data-testid="legal-logo-upload">
              <UploadSimple size={14} /> {uploading ? 'A carregar...' : (legal.livro_reclamacoes_logo_path ? 'Substituir logótipo' : 'Carregar logótipo')}
              <input type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} />
            </label>
            {legal.livro_reclamacoes_logo_path && (
              <button onClick={() => set('livro_reclamacoes_logo_path', '')} className="text-xs text-brand-muted hover:text-red-500" data-testid="legal-logo-remove-btn">Remover</button>
            )}
          </div>
        </div>
        <button onClick={save} disabled={saving} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" data-testid="legal-info-save-btn">{saving ? 'A guardar...' : 'Guardar informação legal'}</button>
      </div>
    </div>
  );
}

function ResourceTypeCategoriesConfigSection() {
  const confirmDelete = useConfirmDelete();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('Tag');

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/resource-type-categories?all=true`); setCats(Array.isArray(data) ? data : []); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Indica o nome da categoria'); return; }
    try { await axios.post(`${API}/api/resource-type-categories`, { name, icon: newIcon }); setNewName(''); setNewIcon('Tag'); toast.success('Categoria adicionada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const rename = (c, name) => setCats(prev => prev.map(x => x.id === c.id ? { ...x, name } : x));
  const commitRename = async (c) => {
    const name = (c.name || '').trim();
    if (!name) { toast.error('Nome obrigatório'); load(); return; }
    try { await axios.put(`${API}/api/resource-type-categories/${c.id}`, { name }); } catch { toast.error('Erro'); load(); }
  };
  const setIcon = async (c, icon) => {
    setCats(prev => prev.map(x => x.id === c.id ? { ...x, icon } : x));
    try { await axios.put(`${API}/api/resource-type-categories/${c.id}`, { icon }); } catch { toast.error('Erro'); load(); }
  };
  const toggleActive = async (c) => { try { await axios.put(`${API}/api/resource-type-categories/${c.id}`, { active: !c.active }); load(); } catch { toast.error('Erro'); } };
  const remove = async (c) => {
    const ok = await confirmDelete({ description: `Vai apagar a categoria "${c.name}". Os recursos associados ficam em "Outros".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/resource-type-categories/${c.id}`); toast.success('Categoria eliminada'); load(); } catch { toast.error('Erro'); }
  };
  const move = async (idx, dir) => {
    const ni = idx + dir; if (ni < 0 || ni >= cats.length) return;
    const out = [...cats]; [out[idx], out[ni]] = [out[ni], out[idx]]; setCats(out);
    try { await axios.put(`${API}/api/resource-type-categories/reorder/all`, { ids: out.map(c => c.id) }); } catch { toast.error('Erro ao reordenar'); load(); }
  };

  if (loading) return null;
  const NewIcon = resourceIcon(newIcon);

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="resource-type-categories-config">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Categorias de tipo (Recomendações)</h3>
      <p className="text-[11px] text-brand-placeholder mb-3">Tipos de recurso (ex.: Livros, Lojas, Códigos de Desconto). Cada recurso pertence a uma categoria; a app da cliente agrupa por estas secções.</p>
      <div className="space-y-2">
        {cats.map((c, idx) => {
          const CIcon = resourceIcon(c.icon);
          return (
            <div key={c.id} className="flex items-center gap-2" data-testid={`rtc-row-${idx}`}>
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-brand-muted hover:text-brand-green disabled:opacity-30 text-xs leading-none">▲</button>
                <button onClick={() => move(idx, 1)} disabled={idx === cats.length - 1} className="text-brand-muted hover:text-brand-green disabled:opacity-30 text-xs leading-none">▼</button>
              </div>
              <div className="w-8 h-8 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center flex-shrink-0"><CIcon size={16} weight="duotone" /></div>
              <select value={c.icon} onChange={e => setIcon(c, e.target.value)} className="border border-brand-border rounded-lg text-xs h-8 px-1 bg-white" data-testid={`rtc-icon-${idx}`}>
                {RESOURCE_ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <Input value={c.name} onChange={e => rename(c, e.target.value)} onBlur={() => commitRename(c)} className="rounded-lg border-brand-border text-xs h-8 flex-1" data-testid={`rtc-name-${idx}`} />
              <button onClick={() => toggleActive(c)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${c.active ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`rtc-toggle-${idx}`}>{c.active ? 'Ativa' : 'Inativa'}</button>
              <button onClick={() => remove(c)} className="text-brand-muted hover:text-red-500 text-xs px-2" data-testid={`rtc-remove-${idx}`}>×</button>
            </div>
          );
        })}
        {cats.length === 0 && <p className="text-xs text-brand-placeholder">Sem categorias configuradas.</p>}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <div className="w-8 h-8 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center flex-shrink-0"><NewIcon size={16} weight="duotone" /></div>
        <select value={newIcon} onChange={e => setNewIcon(e.target.value)} className="border border-brand-border rounded-lg text-xs h-8 px-1 bg-white" data-testid="rtc-new-icon">
          {RESOURCE_ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} placeholder="Nova categoria (ex: Podcasts)" className="rounded-lg border-brand-border text-xs h-8 flex-1" data-testid="rtc-new-input" />
        <button onClick={add} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid="rtc-add-btn">+ Adicionar</button>
      </div>
    </div>
  );
}

